import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import {
  Subscription,
  finalize,
  interval,
} from 'rxjs';

import { ToastService } from '../../../../core/services/toast.service';
import { AuthService } from '../../../../core/services/auth.service';
import {
  AddArtikelDialogComponent,
  AddArtikelOption,
} from '../../components/add-artikel-dialog/add-artikel-dialog.component';
import { BestellassistentComponent } from '../../components/bestellassistent/bestellassistent.component';
import { BestellungArtikel } from '../../models/bestellposition.model';
import { BestellungStatus } from '../../models/bestellung-status.enum';
import {
  BestellungHeader,
  BestellungenService,
  VerfuegbarerArtikel,
} from '../../services/bestellungen.service';
import { BestellungPdfService } from '../../services/bestellung-pdf.service';
import { WarengruppenTabComponent } from '../warengruppen-tab/warengruppen-tab.component';

type ArtikelSortKey =
  | 'nummer'
  | 'name'
  | 'preis'
  | 'vorschlag'
  | 'menge'
  | 'einheit'
  | 'pkg'
  | 'min'
  | 'max';

type SortDirection =
  'asc' | 'desc';

@Component({
  selector: 'app-bestellung-detail',
  standalone: true,

  imports: [
    CommonModule,
    MatIconModule,
    WarengruppenTabComponent,
    AddArtikelDialogComponent,
    BestellassistentComponent,
  ],

  templateUrl:
    './bestellung-detail.component.html',

  styleUrl:
    './bestellung-detail.component.scss',
})
export class BestellungDetailComponent
  implements OnDestroy {

  private readonly router =
    inject(Router);

  private readonly route =
    inject(ActivatedRoute);

  private readonly toastService =
    inject(ToastService);

  private readonly authService =
    inject(AuthService);

  private readonly bestellungenService =
    inject(BestellungenService);

  private readonly elementRef =
    inject(
      ElementRef<HTMLElement>
    );

  private readonly bestellungPdfService =
    inject(
      BestellungPdfService
    );

  protected readonly BestellungStatus =
    BestellungStatus;

  private readonly loadedRecommendations =
    signal<
      Record<string, number>
    >({});

  protected readonly bestellungId =
    signal<string | null>(
      this.route.snapshot.paramMap.get(
        'id'
      )
    );

  protected readonly bestellung =
    signal<BestellungHeader>({
      id: '',
      filialNummer: 0,
      filiale:
        'Bestellung wird geladen...',
      lieferdatum: '—',
      restzeit: '—',
      bestellDatumVon: '',
      bestellDatumBis: '',
      uhrzeitVon: '',
      uhrzeitBis: '',

      orderCanBeChangedContinuously:
        false,

      abgeschlossen:
        false,

      status:
        BestellungStatus.Unbearbeitet,

      lockedByName:
        null,

      gesamtBestellwert:
        0,
    });

  protected readonly artikel =
    signal<
      BestellungArtikel[]
    >([]);

  protected readonly availableArtikel =
    signal<
      AddArtikelOption[]
    >([]);

  protected readonly activeTab =
    signal<
      'artikel' |
      'warengruppen'
    >('artikel');

  protected readonly searchTerm =
    signal('');

  protected readonly selectedArtikelNumber =
    signal<
      string | null
    >(null);

  protected readonly artikelSortKey =
    signal<
      ArtikelSortKey |
      null
    >(null);

  protected readonly artikelSortDirection =
    signal<SortDirection>(
      'asc'
    );

  protected readonly isEditMode =
    signal(false);

  protected readonly isAssistantOpen =
    signal(false);

  protected readonly isCompactView =
    signal(
      window.innerWidth <=
      1280
    );

  protected readonly isLoading =
    signal(false);

  protected readonly skeletonRows =
    Array.from({
      length: 10,
    });

  private readonly currentDateTime =
    signal(
      new Date()
    );

  private readonly editWindowIntervalId =
    window.setInterval(
      () =>
        this.currentDateTime.set(
          new Date()
        ),

      30_000
    );

  protected readonly isSaving =
    signal(false);

  protected readonly isAddArtikelDialogOpen =
    signal(false);

  protected readonly assistantRefreshKey =
    signal(0);

  private editBackup:
    BestellungArtikel[] |
    null = null;

  private autoSaveSubscription?:
    Subscription;

private lockExpiryTimerId:
  number | null = null;

  private isAutoSaving =
    false;

  private lastUnloadSaveAt =
    0;

  private hasReleasedLockBeforeUnload =
    false;

  private readonly AUTO_SAVE_INTERVAL_MS =
    20_000;

  private readonly LOCK_EXPIRY_SAFETY_MS =
    1_500;

  constructor() {
    const id =
      this.bestellungId();

    if (!id) {
      this.router.navigate([
        '/bestellungen',
      ]);

      return;
    }

    this.loadBestellung(
      id,
      false,
      this.authService
        .isPosSession()
    );
  }

  ngOnDestroy(): void {
    window.clearInterval(
      this.editWindowIntervalId
    );

    this.saveChangedPositionenBeforeUnload();

    this.stopAutoSave();
    this.stopLockExpiryTimer();

    const id =
      this.bestellungId();

    if (
      id &&
      this.isEditMode()
    ) {
      this.bestellungenService
        .releaseBestellungLock(
          id
        )
        .subscribe();
    }
  }

  @HostListener(
    'window:beforeunload'
  )
  protected onBeforeUnload(): void {
    this.handlePageUnload();
  }

  @HostListener(
    'window:pagehide'
  )
  protected onPageHide(): void {
    this.handlePageUnload();
  }

  protected readonly filteredArtikel =
    computed(() => {
      const term =
        this.searchTerm()
          .trim()
          .toLowerCase();

      const filtered =
        !term
          ? this.artikel()
          : this.artikel().filter(
            (item) =>
              item.name
                .toLowerCase()
                .includes(
                  term
                ) ||
              item.beschreibung
                .toLowerCase()
                .includes(
                  term
                ) ||
              item.nummer
                .toLowerCase()
                .includes(
                  term
                )
          );

      return this.sortArtikel(
        filtered
      );
    });

  protected readonly invalidArtikel =
    computed(() =>
      this.artikel().filter(
        (item) =>
          item.changed &&
          !!this.getMengeError(
            item
          )
      )
    );

  protected readonly isCurrentTimeInEditWindow =
    computed(() =>
      this.isNowInEditWindow()
    );

  protected readonly canStartEditMode =
    computed(() =>
      !this.bestellung()
        .abgeschlossen &&
      this.isCurrentTimeInEditWindow()
    );

  protected readonly editWindowHint =
    computed(() => {
      const bestellung =
        this.bestellung();

      if (
        bestellung.abgeschlossen
      ) {
        return 'Diese Bestellung ist abgeschlossen und kann nicht mehr bearbeitet werden.';
      }

      if (
        !bestellung.uhrzeitVon ||
        !bestellung.uhrzeitBis
      ) {
        return '';
      }

      if (
        bestellung
          .orderCanBeChangedContinuously
      ) {
        const von =
          this.formatDateTimeForHint(
            bestellung
              .bestellDatumVon,

            bestellung
              .uhrzeitVon
          );

        const bis =
          this.formatDateTimeForHint(
            bestellung
              .bestellDatumBis,

            bestellung
              .uhrzeitBis
          );

        return `Diese Bestellung kann von ${von} bis ${bis} bearbeitet werden.`;
      }

      return `Diese Bestellung kann nur von ${bestellung.uhrzeitVon} Uhr bis ${bestellung.uhrzeitBis} Uhr bearbeitet werden.`;
    });

  protected readonly gesamtbestellwert =
    computed(() => {
      const total =
        this.artikel().reduce(
          (
            sum,
            item
          ) => {
            return (
              sum +
              item.preis *
              this.getActualQuantity(
                item
              )
            );
          },

          0
        );

      return new Intl.NumberFormat(
        'de-DE',
        {
          style:
            'currency',

          currency:
            'EUR',
        }
      ).format(
        total
      );
    });

  protected readonly existingArtikelNumbers =
    computed(() =>
      this.artikel().map(
        (item) =>
          item.nummer
      )
    );

  protected readonly selectedArtikel =
    computed(() => {
      const artikelNumber =
        this.selectedArtikelNumber();

      const artikel =
        this.artikel();

      if (artikelNumber) {
        const selected =
          artikel.find(
            (item) =>
              item.nummer ===
              artikelNumber
          );

        if (selected) {
          return selected;
        }
      }

      return (
        this.sortArtikel(
          artikel
        )[0] ??
        null
      );
    });

  protected readonly activeArtikelNumber =
    computed(() =>
      this.selectedArtikelNumber() ??
      this.selectedArtikel()
        ?.nummer ??
      null
    );

  protected setActiveTab(
    tab:
      'artikel' |
      'warengruppen'
  ): void {
    this.activeTab.set(
      tab
    );
  }

  protected backToBestellungen(): void {
    this.router.navigate([
      '/bestellungen',
    ]);
  }

  protected selectArtikel(
    artikelNumber: string
  ): void {
    this.selectedArtikelNumber.set(
      artikelNumber
    );

    if (
      this.isCompactView()
    ) {
      this.isAssistantOpen.set(
        true
      );
    }
  }

  protected updateSearchTerm(
    event: Event
  ): void {
    const input =
      event.target as
      HTMLInputElement;

    this.searchTerm.set(
      input.value
    );
  }

  protected setArtikelSort(
    key: ArtikelSortKey
  ): void {
    const currentKey =
      this.artikelSortKey();

    if (
      currentKey !== key
    ) {
      this.artikelSortKey.set(
        key
      );

      this.artikelSortDirection.set(
        'asc'
      );

      return;
    }

    if (
      this.artikelSortDirection() ===
      'asc'
    ) {
      this.artikelSortDirection.set(
        'desc'
      );

      return;
    }

    this.artikelSortKey.set(
      null
    );

    this.artikelSortDirection.set(
      'asc'
    );
  }

  protected isArtikelSortActive(
    key: ArtikelSortKey
  ): boolean {
    return (
      this.artikelSortKey() ===
      key
    );
  }

  protected getArtikelSortIcon(
    key: ArtikelSortKey
  ): string {
    if (
      !this.isArtikelSortActive(
        key
      )
    ) {
      return 'unfold_more';
    }

    return (
      this.artikelSortDirection() ===
        'asc'
        ? 'keyboard_arrow_up'
        : 'keyboard_arrow_down'
    );
  }

  protected enableEditMode(): void {
    const id =
      this.bestellungId();

    if (
      !id ||
      !this.canStartEditMode() ||
      this.isLoading()
    ) {
      return;
    }

    this.bestellungenService
      .acquireBestellungLock(
        id
      )
      .subscribe({
        next:
          (
            lockSession
          ) => {
            /*
             * Nach erfolgreichem Lock die Bestellung erneut laden.
             * So beginnt die Bearbeitung immer mit dem aktuellsten Datenstand.
             */
            this.loadBestellung(
              id,
              false,
              false,

              () =>
                this.activateWebEditMode(
                  id,
                  lockSession
                    .expiresAt
                ),

              () => {
                this.bestellungenService
                  .releaseBestellungLock(
                    id
                  )
                  .subscribe();
              }
            );
          },

        error:
          (
            error:
              unknown
          ) => {
            this.toastService.error(
              'Bestellung ist gesperrt',

              this.getErrorMessage(
                error,

                'Die Bestellung kann gerade nicht bearbeitet werden.'
              )
            );
          },
      });
  }

  protected cancelEditMode(): void {
    const id =
      this.bestellungId();

    if (this.editBackup) {
      this.artikel.set(
        structuredClone(
          this.editBackup
        )
      );
    }

    this.closeAddArtikelDialog();
    this.stopLockExpiryTimer();
    this.stopAutoSave();

    if (!id) {
      this.finishEditMode();
      return;
    }

    this.bestellungenService
      .releaseBestellungLock(
        id
      )
      .subscribe({
        next:
          () =>
            this.finishEditMode(),

        error:
          () =>
            this.finishEditMode(),
      });
  }

  protected saveBestellung(): void {
    const id =
      this.bestellungId();

    if (
      !id ||
      this.isSaving()
    ) {
      return;
    }

    if (
      this.invalidArtikel()
        .length > 0
    ) {
      this.toastService.error(
        'Fehlerhafte Mengen',

        'Bitte korrigiere die rot markierten Bestellmengen bevor du speicherst.'
      );

      return;
    }

    const changedPositionen =
      this.getChangedPositionen();

    if (
      !changedPositionen
        .length
    ) {
      this.cancelEditMode();
      return;
    }

    this.isSaving.set(
      true
    );

    this.stopAutoSave();

    this.bestellungenService
      .saveBestellmengenFinal(
        id,
        changedPositionen
      )
      .subscribe({
        next: () => {
          this.markPositionenAsSaved(
            changedPositionen
          );

          this.stopLockExpiryTimer();

          this.bestellungenService
            .releaseBestellungLock(
              id
            )
            .subscribe({
              next:
                () => {
                  this.finishEditMode();

                  this.showBestellungGespeichertToast();
                },

              error:
                () => {
                  this.finishEditMode();

                  this.showBestellungGespeichertToast();
                },
            });
        },

        error:
          (
            error:
              unknown
          ) => {
            this.applyBackendValidationError(
              error
            );

            this.toastService.error(
              'Bestellung konnte nicht gespeichert werden',

              this.getErrorMessage(
                error,

                'Bitte prüfe die Bestellmengen und versuche es erneut.'
              )
            );

            this.isSaving.set(
              false
            );

            this.startAutoSave();
          },
      });
  }

  protected decreaseMenge(
    artikelNummer: string
  ): void {
    const artikel =
      this.findArtikel(
        artikelNummer
      );

    if (
      !artikel ||
      artikel.readonly
    ) {
      return;
    }

    const currentValue =
      artikel.menge ??
      artikel.vorschlag;

    const minValue =
      this.getEffectiveMinValue(
        artikel
      );

    if (
      currentValue <=
      minValue
    ) {
      this.setMenge(
        artikelNummer,
        minValue
      );

      return;
    }

    const nextValue =
      this.normalizeQuantity(
        currentValue -
        artikel.buttonStep
      );

    if (
      nextValue <
      minValue
    ) {
      this.setMenge(
        artikelNummer,
        minValue
      );

      return;
    }

    this.setMenge(
      artikelNummer,
      nextValue
    );
  }

  protected increaseMenge(
    artikelNummer: string
  ): void {
    const artikel =
      this.findArtikel(
        artikelNummer
      );

    if (
      !artikel ||
      artikel.readonly
    ) {
      return;
    }

    const currentValue =
      artikel.menge ??
      artikel.vorschlag;

    const minValue =
      this.getEffectiveMinValue(
        artikel
      );

    if (
      currentValue <= 0 &&
      minValue > 0
    ) {
      this.setMenge(
        artikelNummer,
        minValue
      );

      return;
    }

    const nextValue =
      this.normalizeQuantity(
        currentValue +
        artikel.buttonStep
      );

    const maxValue =
      this.getEffectiveMaxValue(
        artikel
      );

    this.setMenge(
      artikelNummer,

      Math.min(
        maxValue,
        nextValue
      )
    );
  }

  protected updateMengeFromInput(
    artikelNummer: string,
    event: Event
  ): void {
    const artikel =
      this.findArtikel(
        artikelNummer
      );

    const input =
      event.target as
      HTMLInputElement;

    if (
      !artikel ||
      artikel.readonly
    ) {
      return;
    }

    if (
      !input.value.trim()
    ) {
      this.clearMenge(
        artikelNummer
      );

      return;
    }

    const value =
      Number(
        input.value
      );

    if (
      Number.isNaN(
        value
      )
    ) {
      return;
    }

    this.setMenge(
      artikelNummer,
      value
    );
  }

  protected applyRecommendation(
    artikelNummer: string
  ): void {
    const artikel =
      this.findArtikel(
        artikelNummer
      );

    const empfehlung =
      this.loadedRecommendations()[
      artikelNummer
      ];

    if (
      !artikel ||
      artikel.readonly ||
      empfehlung ===
      undefined
    ) {
      return;
    }

    this.setMenge(
      artikelNummer,
      empfehlung
    );
  }

  protected getMengeError(
    artikel:
      BestellungArtikel
  ): string | null {
    if (
      artikel.serverError
    ) {
      return artikel.serverError;
    }

    if (
      artikel.readonly
    ) {
      return null;
    }

    if (
      artikel.menge ===
      null
    ) {
      return artikel.changed
        ? 'Bitte gib eine Bestellmenge ein.'
        : null;
    }

    if (
      artikel.menge < 0
    ) {
      return 'Die Bestellmenge darf nicht negativ sein.';
    }

    if (
      artikel.menge === 0
    ) {
      return null;
    }

    if (
      artikel.quantityStep !==
      null &&
      !this.isMultipleOfStep(
        artikel.menge,
        artikel.quantityStep
      )
    ) {
      return this.getStepErrorMessage(
        artikel
      );
    }

    const minMaxValue =
      this.getMinMaxValidationValue(
        artikel
      );

    if (
      artikel.enforceMinMax &&
      artikel.min !==
      null &&
      minMaxValue <
      artikel.min
    ) {
      return `Mindestmenge: ${this.formatQuantity(
        artikel.min
      )}.`;
    }

    if (
      artikel.enforceMinMax &&
      artikel.max !==
      null &&
      minMaxValue >
      artikel.max
    ) {
      return `Maximalmenge: ${this.formatQuantity(
        artikel.max
      )}.`;
    }

    return null;
  }

  protected getQuantityInputStep(
    artikel:
      BestellungArtikel
  ): string {
    return artikel.inputStep;
  }

  protected getInputMin(
    _artikel:
      BestellungArtikel
  ): number {
    return 0;
  }

  protected getInputMax(
    artikel:
      BestellungArtikel
  ): number | null {
    const max =
      this.getEffectiveMaxValue(
        artikel
      );

    return (
      max ===
        Number.MAX_SAFE_INTEGER
        ? null
        : max
    );
  }

  protected readonly getMengeErrorForView =
    (
      artikel:
        BestellungArtikel
    ): string | null =>
      this.getMengeError(
        artikel
      );

  protected readonly getQuantityInputStepForView =
    (
      artikel:
        BestellungArtikel
    ): string =>
      this.getQuantityInputStep(
        artikel
      );

  protected readonly getInputMinForView =
    (
      artikel:
        BestellungArtikel
    ): number =>
      this.getInputMin(
        artikel
      );

  protected readonly getInputMaxForView =
    (
      artikel:
        BestellungArtikel
    ): number | null =>
      this.getInputMax(
        artikel
      );

  protected readonly getActualQuantityForView =
    (
      artikel:
        BestellungArtikel
    ): number =>
      this.getActualQuantity(
        artikel
      );

  protected getQuantityCalculationLabel(
    artikel:
      BestellungArtikel
  ): string | null {
    if (
      !artikel.showQuantityCalculation ||
      artikel.menge ===
      null ||
      artikel.menge <=
      0
    ) {
      return null;
    }

    const inputQuantity =
      this.normalizeQuantity(
        artikel.menge
      );

    const factor =
      this.normalizeQuantity(
        artikel.calculationFactor
      );

    const actualQuantity =
      this.normalizeQuantity(
        inputQuantity *
        factor
      );

    return `${this.formatQuantity(
      inputQuantity
    )} × ${this.formatQuantity(
      factor
    )} = ${this.formatQuantity(
      actualQuantity
    )}`;
  }

  protected readonly getQuantityCalculationLabelForView =
    (
      artikel:
        BestellungArtikel
    ): string | null =>
      this.getQuantityCalculationLabel(
        artikel
      );

  protected openAddArtikelDialog(): void {
    const id =
      this.bestellungId();

    if (!id) {
      return;
    }

    this.bestellungenService
      .getVerfuegbareArtikel(
        id
      )
      .subscribe({
        next:
          (
            artikel
          ) => {
            this.availableArtikel.set(
              artikel.map(
                (item) =>
                  this.mapAddArtikelOption(
                    item
                  )
              )
            );

            this.isAddArtikelDialogOpen.set(
              true
            );
          },

        error:
          (
            error:
              unknown
          ) => {
            this.toastService.error(
              'Artikel konnten nicht geladen werden',

              this.getErrorMessage(
                error,

                'Bitte versuche es erneut.'
              )
            );
          },
      });
  }

  protected closeAddArtikelDialog(): void {
    this.isAddArtikelDialogOpen.set(
      false
    );
  }

  protected addArtikelToBestellung(
    artikel:
      AddArtikelOption
  ): void {
    const id =
      this.bestellungId();

    if (!id) {
      return;
    }

    this.bestellungenService
      .addArtikel(
        id,

        Number(
          artikel.nummer
        )
      )
      .subscribe({
        next: () => {
          this.closeAddArtikelDialog();

          this.refreshLockAfterChange(
            id
          );

          this.loadBestellung(
            id,
            true
          );

          this.toastService.info(
            `${artikel.name} zur Bestellung hinzugefügt.`,

            'Der Artikel wurde in der Bestellung angelegt.',

            5000
          );
        },

        error:
          (
            error:
              unknown
          ) => {
            this.toastService.error(
              'Artikel konnte nicht hinzugefügt werden',

              this.getErrorMessage(
                error,

                'Bitte versuche es erneut.'
              )
            );
          },
      });
  }

  protected closeAssistant(): void {
    this.isAssistantOpen.set(
      false
    );
  }

  @HostListener(
    'window:resize'
  )
  protected onWindowResize(): void {
    const wasCompact =
      this.isCompactView();

    const isCompact =
      window.innerWidth <
      1280;

    this.isCompactView.set(
      isCompact
    );

    if (isCompact) {
      this.isAssistantOpen.set(
        false
      );

      return;
    }

    if (
      wasCompact &&
      !isCompact &&
      this.selectedArtikelNumber()
    ) {
      this.isAssistantOpen.set(
        true
      );
    }
  }

  private showBestellungGespeichertToast(): void {
    const bestellungId =
      this.bestellungId();

    this.toastService.success(
      'Bearbeitung gespeichert',

      'Die geänderten Bestellmengen wurden gespeichert. Möchten Sie eine Übersicht der bearbeiteten Positionen erstellen?',

      0,

      [
        {
          label:
            'Fertig',

          variant:
            'secondary',

          callback:
            () =>
              undefined,
        },

        {
          label:
            'Übersicht erstellen',

          variant:
            'primary',

          callback:
            () => {
              if (
                !bestellungId
              ) {
                return;
              }

              this.bestellungPdfService
                .openBestelluebersicht(
                  bestellungId
                );
            },
        },
      ]
    );
  }

  private loadBestellung(
    id: string,

    preserveLocalChanges =
      false,

    activatePosEditAfterLoad =
      false,

    onLoaded?:
      () => void,

    onError?:
      () => void
  ): void {
    const changedMengen =
      preserveLocalChanges
        ? new Map(
          this.artikel()
            .filter(
              (item) =>
                item.changed
            )
            .map(
              (item) =>
                [
                  item.nummer,
                  item.menge,
                ] as const
            )
        )
        : new Map<
          string,
          number | null
        >();

    this.isLoading.set(
      true
    );

    this.bestellungenService
      .getBestellungDetail(
        id
      )
      .pipe(
        finalize(() =>
          this.isLoading.set(
            false
          )
        )
      )
      .subscribe({
        next:
          (
            detail
          ) => {
            this.bestellung.set(
              detail.header
            );

            const loadedArtikel =
              detail.artikel.map(
                (item) => {
                  if (
                    !changedMengen.has(
                      item.nummer
                    )
                  ) {
                    return item;
                  }

                  return {
                    ...item,

                    menge:
                      changedMengen.get(
                        item.nummer
                      ) ??
                      null,

                    changed:
                      true,

                    saved:
                      false,
                  };
                }
              );

            this.artikel.set(
              loadedArtikel
            );

            const selectedArtikel =
              this.selectedArtikelNumber();

            const selectedArtikelExists =
              selectedArtikel !==
              null &&
              loadedArtikel.some(
                (item) =>
                  item.nummer ===
                  selectedArtikel
              );

            if (
              !selectedArtikelExists
            ) {
              const firstArtikel =
                this.sortArtikel(
                  loadedArtikel
                )[0];

              this.selectedArtikelNumber.set(
                firstArtikel
                  ?.nummer ??
                null
              );
            }

            if (
              activatePosEditAfterLoad
            ) {
              this.activatePosEditMode(
                id
              );
            }

            onLoaded?.();
          },

        error:
          (
            error:
              unknown
          ) => {
            this.toastService.error(
              'Bestellung konnte nicht geladen werden',

              this.getErrorMessage(
                error,

                'Bitte versuche es erneut.'
              )
            );

            if (
              this.authService
                .isPosSession()
            ) {
              this.bestellungenService
                .releaseBestellungLock(
                  id
                )
                .subscribe({
                  next:
                    () =>
                      this.router.navigate([
                        '/bestellungen',
                      ]),

                  error:
                    () =>
                      this.router.navigate([
                        '/bestellungen',
                      ]),
                });
            }

            onError?.();
          },
      });
  }

  private activateWebEditMode(
    id: string,
    expiresAt: string
  ): void {
    if (
      !this.canStartEditMode()
    ) {
      this.bestellungenService
        .releaseBestellungLock(
          id
        )
        .subscribe();

      return;
    }

    this.editBackup =
      structuredClone(
        this.artikel()
      );

    this.isEditMode.set(
      true
    );

    this.hasReleasedLockBeforeUnload =
      false;

    this.scheduleLockExpiry(
      expiresAt
    );

    this.startAutoSave();

    this.toastService.info(
      'Bearbeitung gestartet',

      'Die Bestellung ist jetzt für andere Benutzer gesperrt.'
    );
  }

  private activatePosEditMode(
    id: string
  ): void {
    if (
      !this.authService
        .isPosSession() ||
      this.isEditMode()
    ) {
      return;
    }

    if (
      !this.canStartEditMode()
    ) {
      this.bestellungenService
        .releaseBestellungLock(
          id
        )
        .subscribe();

      return;
    }

    const lockSession =
      this.bestellungenService
        .getBestellungLockSession(
          id
        );

    if (
      !lockSession
    ) {
      this.router.navigate([
        '/bestellungen',
      ]);

      return;
    }

    this.editBackup =
      structuredClone(
        this.artikel()
      );

    this.isEditMode.set(
      true
    );

    this.hasReleasedLockBeforeUnload =
      false;

    this.scheduleLockExpiry(
      lockSession.expiresAt
    );

    this.startAutoSave();
  }

  private scheduleLockExpiry(
    expiresAt: string
  ): void {
    this.stopLockExpiryTimer();

    const expiresAtMs =
      Date.parse(
        expiresAt
      );

    if (
      Number.isNaN(
        expiresAtMs
      )
    ) {
      return;
    }

    /*
     * Kurz vor dem serverseitigen Redis-Ablauf speichern.
     * Nach expiresAt würde das Backend den Save wegen fehlendem Lock ablehnen.
     */
    const delay =
      Math.max(
        0,

        expiresAtMs -
        Date.now() -
        this.LOCK_EXPIRY_SAFETY_MS
      );

    this.lockExpiryTimerId =
      window.setTimeout(
        () =>
          this.handleLockExpiry(),

        delay
      );
  }

  private stopLockExpiryTimer(): void {
    if (
      this.lockExpiryTimerId ===
      null
    ) {
      return;
    }

    window.clearTimeout(
      this.lockExpiryTimerId
    );

    this.lockExpiryTimerId =
      null;
  }

  private refreshLockAfterChange(
    id: string
  ): void {
    /*
     * Heartbeat ist kein periodischer Keep-Alive mehr.
     * Er wird nur nach einer tatsächlich gespeicherten Änderung gesendet.
     */
    this.stopLockExpiryTimer();

    this.bestellungenService
      .heartbeatBestellungLock(
        id
      )
      .subscribe({
        next:
          (
            lockSession
          ) => {
            if (
              !lockSession
            ) {
              this.handleLostLock(
                id,

                'Die Sperre ist nicht mehr verfügbar.'
              );

              return;
            }

            this.scheduleLockExpiry(
              lockSession
                .expiresAt
            );
          },

        error:
          (
            error:
              unknown
          ) => {
            this.handleLostLock(
              id,

              this.getErrorMessage(
                error,

                'Die Sperre ist abgelaufen oder wurde auf einem anderen Gerät übernommen.'
              )
            );
          },
      });
  }

  private handleLockExpiry(): void {
    const id =
      this.bestellungId();

    if (
      !id ||
      !this.isEditMode() ||
      this.isSaving()
    ) {
      return;
    }

    if (
      this.isAutoSaving
    ) {
      this.lockExpiryTimerId =
        window.setTimeout(
          () =>
            this.handleLockExpiry(),

          250
        );

      return;
    }

    this.stopLockExpiryTimer();
    this.stopAutoSave();

    const changedPositionen =
      this.getChangedPositionen();

    if (
      this.invalidArtikel()
        .length > 0
    ) {
      this.releaseExpiredEditLock(
        id,

        'Die Bearbeitungszeit ist abgelaufen. Ungültige, nicht gespeicherte Eingaben wurden verworfen.'
      );

      return;
    }

    /*
     * Der Backend-Endpunkt /speichern verlangt mindestens eine Position.
     * Wenn Autosave bereits alles gespeichert hat, muss nur der Lock weg.
     */
    if (
      !changedPositionen
        .length
    ) {
      this.releaseExpiredEditLock(
        id,

        'Die Bearbeitungszeit ist abgelaufen. Die Bestellung wurde wieder freigegeben.'
      );

      return;
    }

    this.isSaving.set(
      true
    );

    this.bestellungenService
      .saveBestellmengenFinal(
        id,
        changedPositionen
      )
      .subscribe({
        next:
          () => {
            this.markPositionenAsSaved(
              changedPositionen
            );

            this.releaseExpiredEditLock(
              id,

              'Die letzten Änderungen wurden gespeichert und die Bestellung wurde wieder freigegeben.'
            );
          },

        error:
          (
            error:
              unknown
          ) => {
            this.applyBackendValidationError(
              error
            );

            this.releaseExpiredEditLock(
              id,

              this.getErrorMessage(
                error,

                'Die Bearbeitungszeit ist abgelaufen. Die letzten Änderungen konnten nicht gespeichert werden.'
              )
            );
          },
      });
  }

  private releaseExpiredEditLock(
    id: string,
    message: string
  ): void {
    this.bestellungenService
      .releaseBestellungLock(
        id
      )
      .subscribe({
        next:
          () =>
            this.finishExpiredEdit(
              message
            ),

        error:
          () =>
            this.finishExpiredEdit(
              message
            ),
      });
  }

  private finishExpiredEdit(
    message: string
  ): void {
    const id =
      this.bestellungId();

    const isPosSession =
      this.authService
        .isPosSession();

    this.finishEditMode();

    if (
      !isPosSession &&
      id
    ) {
      this.loadBestellung(
        id
      );
    }

    this.toastService.warning(
      'Bearbeitung beendet',
      message
    );
  }

  private handleLostLock(
    id: string,
    message: string
  ): void {
    const isPosSession =
      this.authService
        .isPosSession();

    this.stopAutoSave();
    this.stopLockExpiryTimer();

    this.bestellungenService
      .clearBestellungLock(
        id
      );

    this.finishEditMode();

    if (
      !isPosSession
    ) {
      this.loadBestellung(
        id
      );
    }

    this.toastService.warning(
      'Sperre nicht mehr aktiv',
      message
    );
  }

  private startAutoSave(): void {
    const id =
      this.bestellungId();

    this.stopAutoSave();

    if (!id) {
      return;
    }

    this.autoSaveSubscription =
      interval(
        this.AUTO_SAVE_INTERVAL_MS
      ).subscribe(
        () => {
          this.saveChangedPositionen(
            id
          );
        }
      );
  }

  private stopAutoSave(): void {
    this.autoSaveSubscription
      ?.unsubscribe();

    this.autoSaveSubscription =
      undefined;
  }

  private saveChangedPositionen(
    id: string
  ): void {
    if (
      this.isAutoSaving ||
      this.invalidArtikel()
        .length > 0
    ) {
      return;
    }

    const changedPositionen =
      this.getChangedPositionen();

    if (
      !changedPositionen
        .length
    ) {
      return;
    }

    this.isAutoSaving =
      true;

    this.bestellungenService
      .saveBestellmengen(
        id,
        changedPositionen
      )
      .subscribe({
        next:
          () => {
            this.markPositionenAsSaved(
              changedPositionen
            );

            this.refreshLockAfterChange(
              id
            );
          },

        error:
          (
            error:
              unknown
          ) => {
            this.applyBackendValidationError(
              error
            );

            this.isAutoSaving =
              false;
          },

        complete:
          () => {
            this.isAutoSaving =
              false;
          },
      });
  }

  private finishEditMode(): void {
    this.stopAutoSave();
    this.stopLockExpiryTimer();

    this.editBackup =
      null;

    this.isEditMode.set(
      false
    );

    this.isSaving.set(
      false
    );

    this.closeAddArtikelDialog();

    if (
      this.authService
        .isPosSession()
    ) {
      this.router.navigate([
        '/bestellungen',
      ]);
    }
  }

  private findArtikel(
    artikelNummer: string
  ): BestellungArtikel | undefined {
    return this.artikel().find(
      (item) =>
        item.nummer ===
        artikelNummer
    );
  }

  private isMultipleOfStep(
    value: number,
    step: number
  ): boolean {
    const remainder =
      value % step;

    return (
      Math.abs(
        remainder
      ) <
      0.000001 ||
      Math.abs(
        remainder -
        step
      ) <
      0.000001
    );
  }

  private setMenge(
    artikelNummer: string,
    menge: number
  ): void {
    this.artikel.update(
      (items) =>
        items.map(
          (item) =>
            item.nummer ===
              artikelNummer
              ? {
                ...item,

                menge:
                  this.normalizeQuantity(
                    menge
                  ),

                changed:
                  true,

                saved:
                  false,

                serverError:
                  null,
              }
              : item
        )
    );
  }

  private clearMenge(
    artikelNummer: string
  ): void {
    this.artikel.update(
      (items) =>
        items.map(
          (item) =>
            item.nummer ===
              artikelNummer
              ? {
                ...item,

                menge:
                  null,

                changed:
                  true,

                saved:
                  false,

                serverError:
                  null,
              }
              : item
        )
    );
  }

  private mapAddArtikelOption(
    artikel:
      VerfuegbarerArtikel
  ): AddArtikelOption {
    const losgroesse =
      artikel.bestellmenge ??
      1;

    const einheit =
      `Stück / ${losgroesse}`;

    return {
      nummer:
        String(
          artikel.artikelnummer
        ),

      name:
        artikel.artikelName ??
        `Artikel ${artikel.artikelnummer}`,

      beschreibung:
        `${artikel.warengruppeBezeichnung ??
        'Verfügbarer Artikel'} • ${this.formatPrice(
          artikel.vkPreis ?? 0
        )} € • ${einheit}`,

      preis:
        artikel.vkPreis ??
        0,

      vorschlag:
        artikel.bestellmenge ??
        0,

      empfehlung:
        artikel.bestellmenge ??
        0,

      einheit,

      pkg:
        artikel.halbePkgErlaubt,

      min:
        artikel.bestellmenge,

      max:
        null,
    };
  }

  private formatPrice(
    value: number
  ): string {
    return new Intl.NumberFormat(
      'de-DE',
      {
        minimumFractionDigits:
          2,

        maximumFractionDigits:
          2,
      }
    ).format(
      value
    );
  }

  private getErrorMessage(
    error: unknown,
    fallback: string
  ): string {
    const message =
      this.extractErrorMessage(
        error
      );

    return (
      message?.trim() ||
      fallback
    );
  }

  private extractErrorMessage(
    value: unknown,
    depth = 0
  ): string | null {
    if (
      depth > 4 ||
      value === null ||
      value === undefined
    ) {
      return null;
    }

    if (
      typeof value ===
      'string'
    ) {
      return value;
    }

    if (
      Array.isArray(
        value
      )
    ) {
      const messages =
        value
          .map(
            (item) =>
              this.extractErrorMessage(
                item,
                depth + 1
              )
          )
          .filter(
            (
              item
            ): item is string =>
              !!item
          );

      return messages.length
        ? messages.join(
          '\n'
        )
        : null;
    }

    if (
      typeof value !==
      'object'
    ) {
      return null;
    }

    const record =
      value as Record<
        string,
        unknown
      >;

    return (
      this.extractErrorMessage(
        record['error'],
        depth + 1
      ) ??
      this.extractErrorMessage(
        record['detail'],
        depth + 1
      ) ??
      this.extractErrorMessage(
        record['message'],
        depth + 1
      ) ??
      this.extractValidationErrors(
        record['errors']
      ) ??
      this.extractErrorMessage(
        record['title'],
        depth + 1
      )
    );
  }

  private extractValidationErrors(
    value: unknown
  ): string | null {
    if (
      !value ||
      typeof value !==
      'object'
    ) {
      return null;
    }

    const messages =
      Object.values(
        value as Record<
          string,
          unknown
        >
      )
        .flatMap(
          (item) =>
            Array.isArray(
              item
            )
              ? item
              : [item]
        )
        .filter(
          (
            item
          ): item is string =>
            typeof item ===
            'string'
        );

    return messages.length
      ? messages.join(
        '\n'
      )
      : null;
  }

  private sortArtikel(
    items:
      BestellungArtikel[]
  ): BestellungArtikel[] {
    const key =
      this.artikelSortKey();

    if (
      key === null
    ) {
      return [
        ...items,
      ];
    }

    const direction =
      this.artikelSortDirection();

    return [
      ...items,
    ].sort(
      (
        a,
        b
      ) => {
        const result =
          this.compareArtikelValue(
            a,
            b,
            key
          );

        return (
          direction ===
            'asc'
            ? result
            : -result
        );
      }
    );
  }

  private compareArtikelValue(
    a:
      BestellungArtikel,

    b:
      BestellungArtikel,

    key:
      ArtikelSortKey
  ): number {
    switch (key) {
      case 'nummer':
        return this.compareNumber(
          Number(
            a.nummer
          ),

          Number(
            b.nummer
          )
        );

      case 'name':
        return this.compareText(
          a.name,
          b.name
        );

      case 'preis':
        return this.compareNumber(
          a.preis,
          b.preis
        );

      case 'vorschlag':
        return this.compareNumber(
          a.vorschlag,
          b.vorschlag
        );

      case 'menge':
        return this.compareNumber(
          a.menge ??
          a.vorschlag,

          b.menge ??
          b.vorschlag
        );

      case 'einheit':
        return this.compareText(
          a.einheit,
          b.einheit
        );

      case 'pkg':
        return this.compareNumber(
          Number(
            a.pkg
          ),

          Number(
            b.pkg
          )
        );

      case 'min':
        return this.compareNullableNumber(
          a.min,
          b.min
        );

      case 'max':
        return this.compareNullableNumber(
          a.max,
          b.max
        );
    }
  }

  private compareText(
    a: string,
    b: string
  ): number {
    return a.localeCompare(
      b,
      'de',
      {
        numeric:
          true,

        sensitivity:
          'base',
      }
    );
  }

  private compareNumber(
    a: number,
    b: number
  ): number {
    return a - b;
  }

  private compareNullableNumber(
    a:
      number |
      null,

    b:
      number |
      null
  ): number {
    if (
      a === null &&
      b === null
    ) {
      return 0;
    }

    if (
      a === null
    ) {
      return 1;
    }

    if (
      b === null
    ) {
      return -1;
    }

    return a - b;
  }

  private saveChangedPositionenBeforeUnload(): void {
    const id =
      this.bestellungId();

    if (
      !id ||
      !this.isEditMode()
    ) {
      return;
    }

    const now =
      Date.now();

    if (
      now -
      this.lastUnloadSaveAt <
      1000
    ) {
      return;
    }

    if (
      this.invalidArtikel()
        .length > 0
    ) {
      return;
    }

    const changedPositionen =
      this.getChangedPositionen();

    if (
      !changedPositionen
        .length
    ) {
      return;
    }

    this.lastUnloadSaveAt =
      now;

    this.bestellungenService
      .saveBestellmengenBeforeUnload(
        id,
        changedPositionen
      );
  }

  private handlePageUnload(): void {
    const id =
      this.bestellungId();

    this.saveChangedPositionenBeforeUnload();

    this.stopAutoSave();
    this.stopLockExpiryTimer();

    if (
      !id ||
      !this.isEditMode()
    ) {
      return;
    }

    this.releaseLockBeforeUnload(
      id
    );
  }

  private releaseLockBeforeUnload(
    id: string
  ): void {
    if (
      this.hasReleasedLockBeforeUnload
    ) {
      return;
    }

    this.hasReleasedLockBeforeUnload =
      true;

    this.bestellungenService
      .releaseBestellungLockBeforeUnload(
        id
      );
  }

  private getChangedPositionen(): {
    artikelnummer:
    number;

    menge:
    number;
  }[] {
    return this.artikel()
      .filter(
        (item) =>
          item.changed &&
          !item.readonly
      )
      .map(
        (item) => ({
          artikelnummer:
            Number(
              item.nummer
            ),

          menge:
            item.menge ??
            0,
        })
      )
      .filter(
        (item) =>
          !Number.isNaN(
            item.artikelnummer
          )
      );
  }

  private markPositionenAsSaved(
    savedPositionen: {
      artikelnummer:
      number;

      menge:
      number;
    }[]
  ): void {
    const savedMap =
      new Map(
        savedPositionen.map(
          (item) => [
            String(
              item.artikelnummer
            ),

            item.menge,
          ]
        )
      );

    this.artikel.update(
      (items) =>
        items.map(
          (item) => {
            const savedMenge =
              savedMap.get(
                item.nummer
              );

            if (
              savedMenge ===
              undefined
            ) {
              return item;
            }

            if (
              (
                item.menge ??
                0
              ) !==
              savedMenge
            ) {
              return item;
            }

            return {
              ...item,

              changed:
                false,

              saved:
                true,

              serverError:
                null,
            };
          }
        )
    );
  }

  protected focusNextQuantityInput(
    event: Event
  ): void {
    event.preventDefault();
    event.stopPropagation();

    const currentInput =
      event.target;

    if (
      !(
        currentInput instanceof
        HTMLInputElement
      )
    ) {
      return;
    }

    currentInput.dispatchEvent(
      new Event(
        'input',
        {
          bubbles:
            true,
        }
      )
    );

    const hostElement =
      this.elementRef
        .nativeElement as
      HTMLElement;

    const quantityInputs =
      Array.from(
        hostElement
          .querySelectorAll(
            '.tab-content-container .quantity-input'
          )
      ).filter(
        (
          input
        ): input is HTMLInputElement =>
          input instanceof
          HTMLInputElement &&
          !input.disabled &&
          input.offsetParent !==
          null
      );

    const currentIndex =
      quantityInputs.indexOf(
        currentInput
      );

    const nextInput =
      quantityInputs[
      currentIndex +
      1
      ];

    if (!nextInput) {
      currentInput.blur();
      return;
    }

    requestAnimationFrame(
      () => {
        nextInput.focus();
        nextInput.select();
      }
    );
  }

  private normalizeQuantity(
    value: number
  ): number {
    return Number(
      value.toFixed(
        6
      )
    );
  }

  private getActualQuantity(
    artikel:
      BestellungArtikel
  ): number {
    return this.normalizeQuantity(
      (
        artikel.menge ??
        0
      ) *
      artikel.calculationFactor
    );
  }

  private getMinMaxValidationValue(
    artikel:
      BestellungArtikel
  ): number {
    return this.normalizeQuantity(
      (
        artikel.menge ??
        0
      ) *
      artikel.minMaxValidationFactor
    );
  }

  private getEffectiveMinValue(
    artikel:
      BestellungArtikel
  ): number {
    const step =
      artikel.buttonStep ||
      1;

    if (
      !artikel.enforceMinMax ||
      artikel.min ===
      null
    ) {
      return step;
    }

    const rawMin =
      artikel.min /
      artikel.minMaxValidationFactor;

    const minByStep =
      this.ceilToStep(
        rawMin,
        step
      );

    return Math.max(
      step,
      minByStep
    );
  }

  private getEffectiveMaxValue(
    artikel:
      BestellungArtikel
  ): number {
    const step =
      artikel.buttonStep ||
      1;

    if (
      !artikel.enforceMinMax ||
      artikel.max ===
      null
    ) {
      return Number.MAX_SAFE_INTEGER;
    }

    const rawMax =
      artikel.max /
      artikel.minMaxValidationFactor;

    return this.floorToStep(
      rawMax,
      step
    );
  }

  private ceilToStep(
    value: number,
    step: number
  ): number {
    return this.normalizeQuantity(
      Math.ceil(
        value /
        step
      ) *
      step
    );
  }

  private floorToStep(
    value: number,
    step: number
  ): number {
    return this.normalizeQuantity(
      Math.floor(
        value /
        step
      ) *
      step
    );
  }

  private getStepErrorMessage(
    artikel:
      BestellungArtikel
  ): string {
    return artikel.quantityStepLabel;
  }

  private formatQuantity(
    value: number
  ): string {
    return new Intl.NumberFormat(
      'de-DE',
      {
        maximumFractionDigits:
          2,
      }
    ).format(
      value
    );
  }

  private applyBackendValidationError(
    error: unknown
  ): void {
    const message =
      this.getErrorMessage(
        error,
        ''
      );

    if (!message) {
      return;
    }

    const validationError =
      this.parseBackendArtikelError(
        message
      );

    if (
      !validationError
    ) {
      return;
    }

    this.artikel.update(
      (items) =>
        items.map(
          (item) =>
            item.nummer ===
              validationError
                .artikelNummer
              ? {
                ...item,

                changed:
                  true,

                saved:
                  false,

                serverError:
                  validationError
                    .message,
              }
              : item
        )
    );

    this.selectedArtikelNumber.set(
      validationError
        .artikelNummer
    );
  }

  private parseBackendArtikelError(
    message: string
  ): {
    artikelNummer:
    string;

    message:
    string;
  } | null {
    const match =
      /Artikel\s+(\d+)\s*:\s*(.+)/i.exec(
        message
      );

    if (!match) {
      return null;
    }

    const [
      ,
      artikelNummer,
      detailMessage,
    ] =
      match;

    return {
      artikelNummer,
      message:
        detailMessage,
    };
  }

  private isNowInEditWindow(): boolean {
    const bestellung =
      this.bestellung();

    if (
      !bestellung.uhrzeitVon ||
      !bestellung.uhrzeitBis
    ) {
      return true;
    }

    if (
      !bestellung.bestellDatumVon ||
      !bestellung.bestellDatumBis
    ) {
      return this.isCurrentTimeInDailyWindow();
    }

    const now =
      this.currentDateTime();

    if (
      bestellung
        .orderCanBeChangedContinuously
    ) {
      const start =
        this.combineDateAndTime(
          bestellung
            .bestellDatumVon,

          bestellung
            .uhrzeitVon
        );

      const end =
        this.combineDateAndTime(
          bestellung
            .bestellDatumBis,

          bestellung
            .uhrzeitBis
        );

      if (
        !start ||
        !end
      ) {
        return true;
      }

      return (
        now >= start &&
        now <= end
      );
    }

    const currentDate =
      this.resetDateTime(
        now
      );

    const startDate =
      this.parseIsoDateOnly(
        bestellung
          .bestellDatumVon
      );

    const endDate =
      this.parseIsoDateOnly(
        bestellung
          .bestellDatumBis
      );

    if (
      !startDate ||
      !endDate
    ) {
      return this.isCurrentTimeInDailyWindow();
    }

    return (
      currentDate >=
      startDate &&
      currentDate <=
      endDate &&
      this.isCurrentTimeInDailyWindow()
    );
  }

  private isCurrentTimeInDailyWindow(): boolean {
    const {
      uhrzeitVon,
      uhrzeitBis,
    } =
      this.bestellung();

    const fromMinutes =
      this.parseTimeToMinutes(
        uhrzeitVon
      );

    const toMinutes =
      this.parseTimeToMinutes(
        uhrzeitBis
      );

    if (
      fromMinutes ===
      null ||
      toMinutes ===
      null
    ) {
      return true;
    }

    const currentMinutes =
      this.getCurrentMinutes();

    if (
      fromMinutes <=
      toMinutes
    ) {
      return (
        currentMinutes >=
        fromMinutes &&
        currentMinutes <=
        toMinutes
      );
    }

    return (
      currentMinutes >=
      fromMinutes ||
      currentMinutes <=
      toMinutes
    );
  }

  private combineDateAndTime(
    dateValue: string,
    timeValue: string
  ): Date | null {
    const date =
      this.parseIsoDateOnly(
        dateValue
      );

    const minutes =
      this.parseTimeToMinutes(
        timeValue
      );

    if (
      !date ||
      minutes ===
      null
    ) {
      return null;
    }

    date.setHours(
      Math.floor(
        minutes /
        60
      ),

      minutes %
      60,

      0,
      0
    );

    return date;
  }

  private parseIsoDateOnly(
    value: string
  ): Date | null {
    const [
      year,
      month,
      day,
    ] =
      value
        .split('-')
        .map(Number);

    if (
      !year ||
      !month ||
      !day
    ) {
      return null;
    }

    return new Date(
      year,
      month - 1,
      day
    );
  }

  private resetDateTime(
    value: Date
  ): Date {
    const date =
      new Date(
        value
      );

    date.setHours(
      0,
      0,
      0,
      0
    );

    return date;
  }

  private parseTimeToMinutes(
    value: string
  ): number | null {
    const [
      hours,
      minutes,
    ] =
      value
        .split(':')
        .map(Number);

    if (
      Number.isNaN(
        hours
      ) ||
      Number.isNaN(
        minutes
      ) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    return (
      hours * 60 +
      minutes
    );
  }

  private getCurrentMinutes(): number {
    const now =
      this.currentDateTime();

    return (
      now.getHours() *
      60 +
      now.getMinutes()
    );
  }

  private formatDateTimeForHint(
    dateValue: string,
    timeValue: string
  ): string {
    const date =
      this.parseIsoDateOnly(
        dateValue
      );

    if (!date) {
      return `${dateValue} ${timeValue} Uhr`;
    }

    return `${new Intl.DateTimeFormat(
      'de-DE',
      {
        day:
          '2-digit',

        month:
          '2-digit',

        year:
          'numeric',
      }
    ).format(date)} ${timeValue} Uhr`;
  }

  protected setLoadedRecommendation(
    event: {
      artikelNummer:
      string;

      menge:
      number;
    }
  ): void {
    this.loadedRecommendations.update(
      (current) => ({
        ...current,

        [event.artikelNummer]:
          event.menge,
      })
    );
  }

  protected hasLoadedRecommendation(
    artikelNummer: string
  ): boolean {
    return (
      this.loadedRecommendations()[
      artikelNummer
      ] !== undefined
    );
  }
}